import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import AuthService, { type SignUpRequest, type SignUpResponse } from "@/services/api/auth";

export interface SignUpState {
  user: SignUpResponse | null;
  isLoading: boolean;
  error: string | null;
  isSuccess: boolean;
  message: string | null;
}

const initialState: SignUpState = {
  user: null,
  isLoading: false,
  error: null,
  isSuccess: false,
  message: null,
};

export const signUpUser = createAsyncThunk(
  "signup/signUpUser",
  async (data: SignUpRequest, { rejectWithValue }) => {
    try {
      const response = await AuthService.signup(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || "Sign up failed",
      );
    }
  },
);

const signupSlice = createSlice({
  name: "signup",
  initialState,
  reducers: {
    clearSignupError: (state) => {
      state.error = null;
    },
    clearSignupSuccess: (state) => {
      state.isSuccess = false;
      state.message = null;
    },
    resetSignupState: (state) => {
      state.user = null;
      state.isLoading = false;
      state.error = null;
      state.isSuccess = false;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signUpUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signUpUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isSuccess = true;
        state.error = null;
        state.message = "Sign up successful";
      })
      .addCase(signUpUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isSuccess = false;
      });
  },
});

export const { clearSignupError, clearSignupSuccess, resetSignupState } = signupSlice.actions;
export default signupSlice.reducer;
